/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable sonarjs/no-duplicate-string */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment-timezone';
import queryString from 'query-string';

import { colors } from 'styles';
import EmptyTrunk from 'components/animations/EmptyTrunk';
import PillarsAnimations from 'components/animations/PillarsAnimations';
import Dropdown from 'components/Dropdown';
import ColumnMiniChart from 'components/charts/ColumnMiniChart';
import LineMiniChart from 'components/charts/LineMiniChart';
import Loading from 'components/animations/Loading';
import useSenslaApi from 'hooks/useSenslaApi';
import SenslaChart from 'components/SenslaChart';

import { ctx } from 'store';

import { useStyles } from './styles';

// Counters
import ActivityCounter from '../counters/ActivityCounter';
import ActiveMembersCounter from '../counters/ActiveMembersCounter';
import MoodCounter from '../counters/MoodCounter';
import MentionsCounter from '../counters/MentionsCounter';
import TimeCounter from '../counters/TimeCounter';

const PillarsReport: React.FC = () => {
  const {
    teams: { currentTeam },
  } = useContext(ctx.store);
  const location = useLocation();
  const classes = useStyles();
  const history = useHistory();
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [daysSelected, setDaysSelected] = useState(7);
  const [channels, setChannels] = useState<{ id: string }[]>([]);
  const [groups, setGroups] = useState<{ id: string }[]>([]);
  const queryParams: any = queryString.parse(location.search);
  const { channelId, groupId } = queryParams;
  const [selectedQuery, setSelectedQuery] = useState('');
  const [channel, setChannel] = useState(channelId ? channelId : 'All-channels');
  const [group, setGroup] = useState(groupId ? groupId : 'All-teams');

  const query = queryString.stringify(
    {
      days: daysSelected,
      channel: channel === 'All-channels' ? null : channel,
      group: group === 'All-teams' ? null : group,
      team: currentTeam.teamId ? currentTeam.teamId : currentTeam.tenantId,
      platform: currentTeam.teamId ? 'slack' : 'microsoft',
    },
    {
      skipNull: true,
    }
  );

  const [{ data: activityReport, loading }] = useSenslaApi(
    `activities/report/pillars?${query}`,
    'GET',
    true
  );

  useEffect(() => {
    const data = [
      ...(currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels).map((ch: any) => {
        return { id: ch.channelId };
      }),
    ];
    const groupsData = [
      ...(currentTeam.teamId ? [] : currentTeam.msGroups)
        .filter((gr: any) => gr.monitored)
        .map((group: any) => {
          return { id: group.groupId };
        }),
    ];
    onChange(channelId, 'channels', true);
    setSelectedChannel(channelId);
    setChannels(data);
    if (!currentTeam.teamId) {
      onChange(groupId, 'teams', true);
      setSelectedGroup(groupId);
      setGroups(groupsData);
    }
    setDaysSelected(7);
  }, []);

  // Counters
  const onChange = (value: string, type: string, fromEffect?: boolean) => {
    if (!fromEffect) {
      let groupQuery =
        group || type === 'teams' ? `groupId=${type === 'teams' ? value : group}` : '';
      let channelQuery =
        channel || type === 'channels' ? `channelId=${type === 'channels' ? value : channel}` : '';

      groupQuery = groupQuery === 'groupId=All-teams' ? '' : groupQuery;
      channelQuery = channelQuery === 'channelId=All-channels' ? '' : channelQuery;

      let completeQuery = '';
      if (groupQuery !== '' && channelQuery !== '') {
        completeQuery = `?${groupQuery}&${channelQuery}`;
      } else if (groupQuery === '') {
        completeQuery = `?${channelQuery}`;
      } else if (channelQuery === '') {
        completeQuery = `?${groupQuery}`;
      }

      setSelectedQuery(completeQuery);

      history.push(`/dashboard/pillars-report${completeQuery}`);
    }

    if (type === 'channels') {
      setSelectedChannel(value);
      const filteredChannel = (currentTeam.teamId
        ? currentTeam.channels
        : currentTeam.msChannels
      ).filter((channel: any) => channel.channelId === value)[0];
      if (filteredChannel) {
        setChannel(filteredChannel.channelId);
      } else {
        setChannel('');
      }
    } else if (type === 'teams') {
      setSelectedGroup(value);
      const filteredGroup = currentTeam.msGroups.filter((group: any) => group.groupId === value)[0];
      if (filteredGroup) {
        setGroup(filteredGroup.groupId);
      } else {
        setGroup('');
      }
    }
  };

  const onChangeDate = (value: any) => {
    setDaysSelected(value);
  };

  const dropdownChannels = useMemo(() => {
    if (!groupId || groupId === '') {
      return channels;
    }
    return currentTeam.msGroups
      .find((group: any) => group.groupId === groupId)
      .channels.map((channel: any) => ({ id: channel }));
  }, [groupId, channels]);

  const activityByWeek = (data: any) => {
    const days = [1, 2, 3, 4, 5, 6, 7];
    const dataArray = [];
    for (const day of days) {
      const weekDay = data.filter((ac: any) => ac.dayOfWeek === day);
      if (weekDay.length > 0) {
        dataArray.push(weekDay[0]);
      } else {
        dataArray.push({ _id: day, dayOfWeek: day, count: 0, avg: 0, total: 0 });
      }
    }
    return dataArray;
  };

  const dataByDates = (data: any, type: 'activity' | 'mentions', compare?: boolean) => {
    const startDate = moment()
      .subtract(!compare ? daysSelected : daysSelected * 2, 'days')
      .format('MM/DD');
    const datesArray = [];
    for (let i = 0; i < daysSelected + 1; i++) {
      const date = moment(startDate).add(i, 'days').format('MM/DD');
      datesArray.push(date);
    }
    const dataArray = [];
    for (const date of datesArray) {
      const evaluatedDate = data.filter(
        (ac: any) => moment(`${ac.month}/${ac.dayOfMonth}`).format('MM/DD') === date
      );

      if (evaluatedDate.length > 0) {
        dataArray.push(evaluatedDate[0]);
      } else {
        dataArray.push(
          type === 'activity'
            ? {
                _id: date,
                dayOfMonth: moment(date).format('DD'),
                month: moment(date).format('MM'),
                count: 0,
                avg: 0,
              }
            : {
                _id: date,
                dayOfMonth: moment(date).format('DD'),
                month: moment(date).format('MM'),
                count: 0,
                avgTime: 0,
                respondedCount: 0,
              }
        );
      }
    }
    return dataArray;
  };

  return (
    <div style={{ height: '100vh' }}>
      <Box display="flex" p={1} alignItems="center" className={classes.header}>
        <Box p={1} flexGrow={1}>
          <Typography component="h1" variant="h5">
            Pillar Details
          </Typography>
        </Box>
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            data={[{ id: 7 }, { id: 14 }, { id: 30 }, { id: 90 }]}
            translate={'days'}
            type="days"
            setValue={onChangeDate}
            value={String(daysSelected)}
            defaultValue={String(daysSelected)}
          />
        </Box>
        {!currentTeam.teamId ? (
          <Box p={1} flexShrink={0} className={classes.channelSelect}>
            <Dropdown
              addWorkspace
              data={groups}
              translate={currentTeam.msGroups}
              type="teams"
              setValue={onChange}
              value={selectedGroup}
              autocomplete
              defaultValue={groupId}
            />
          </Box>
        ) : null}
        <Box p={1} flexShrink={0} className={classes.channelSelect}>
          <Dropdown
            addWorkspace
            data={dropdownChannels}
            translate={currentTeam.teamId ? currentTeam.channels : currentTeam.msChannels}
            type="channels"
            setValue={onChange}
            value={selectedChannel}
            autocomplete
            defaultValue={channelId}
          />
        </Box>
      </Box>
      {!loading && activityReport ? (
        <>
          {activityReport &&
          activityReport.channelActivity &&
          activityReport.channelActivity.length === 0 ? (
            <div className={classes.noData}>
              <Typography component="h2">{`No data to be displayed in the last ${daysSelected} days!`}</Typography>
              <EmptyTrunk animationWidth={400} animationHeight={400} containerHeight="initial" />
            </div>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper className={classes.papersColumn}>
                    <Grid
                      style={{
                        alignItems: 'center',
                        borderBottom: `2px solid ${colors.greyBg}`,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        padding: '5px 0',
                      }}
                    >
                      <Typography component="h2" className={classes.subtitle}>
                        {`ENERGY: ${
                          activityReport.pillars.length
                            ? activityReport.pillars[activityReport.pillars.length - 1].energy > 0.8
                              ? 'Excellent Job'
                              : activityReport.pillars[activityReport.pillars.length - 1].energy >
                                0.6
                              ? 'Good Job'
                              : activityReport.pillars[activityReport.pillars.length - 1].energy >
                                0.4
                              ? 'It Should Be Better'
                              : 'We Must Do Better'
                            : ''
                        }`}
                      </Typography>
                      <Typography
                        onClick={() => history.push(`/dashboard/reports${selectedQuery}`)}
                        component="h2"
                        className={classes.ctaText}
                      >
                        Advanced Channel View <ArrowRightIcon fontSize="large" />
                      </Typography>
                    </Grid>
                    <Grid className={classes.gridRow}>
                      <Grid xs={4} className={classes.titleGrid}>
                        <Grid className={classes.gridRow}>
                          <PillarsAnimations speed={0.5} type="energy" containerWidth="45%" />
                          <SenslaChart
                            data={
                              activityReport
                                ? [activityReport.pillars[activityReport.pillars.length - 1].energy]
                                : []
                            }
                            name="Energy"
                            color={colors.orangeBrand}
                          />
                        </Grid>
                        <Alert severity={'warning'} icon={false}>
                          The Energy Metric represents the way that your team is participating. The
                          metric is based on activity according to the group's size and the
                          sentiment of the messages. A low Energy can represent a lack of
                          participation, absence of members, or irregular situations during the
                          week.
                        </Alert>
                      </Grid>
                      <Grid xs={2}>
                        <ActivityCounter
                          activityReport={activityReport}
                          withoutPaper={true}
                          withoutIcon={true}
                        />
                        <div
                          style={{
                            border: `1px solid ${colors.textGrey3e}`,
                            opacity: 0.7,
                            width: '100%',
                          }}
                        />
                        <MoodCounter
                          activityReport={activityReport}
                          withoutPaper={true}
                          withoutIcon={true}
                          withoutChip={true}
                        />
                      </Grid>
                      <Grid xs={6}>
                        <LineMiniChart
                          data={
                            activityReport
                              ? dataByDates(activityReport.datesActivity, 'activity').map(
                                  (item: any) => {
                                    return {
                                      x: moment(`${item.month}/${item.dayOfMonth}`),
                                      y: item.count,
                                    };
                                  }
                                )
                              : []
                          }
                          compareData={
                            activityReport
                              ? dataByDates(
                                  activityReport.previousDatesActivity,
                                  'activity',
                                  true
                                ).map((item: any) => {
                                  return {
                                    x: moment(`${item.month}/${item.dayOfMonth}`),
                                    y: item.count,
                                  };
                                })
                              : []
                          }
                          seriesName={['Activity', 'Previous Activity']}
                          color={colors.orangeBrand}
                          isNumber={true}
                          height={150}
                          width={'100%'}
                          strokeWidth={3}
                          showLabelOff={true}
                          legendPosition="top"
                        />
                        <div
                          style={{
                            border: `1px solid ${colors.textGrey3e}`,
                            opacity: 0.7,
                            width: '100%',
                          }}
                        />
                        <ColumnMiniChart
                          data={
                            activityReport
                              ? dataByDates(activityReport.datesActivity, 'activity').map(
                                  (item: any) => {
                                    return {
                                      x: moment(`${item.month}/${item.dayOfMonth}`),
                                      y: item.avg,
                                    };
                                  }
                                )
                              : []
                          }
                          compareData={
                            activityReport
                              ? dataByDates(
                                  activityReport.previousDatesActivity,
                                  'activity',
                                  true
                                ).map((item: any) => {
                                  return {
                                    x: moment(`${item.month}/${item.dayOfMonth}`),
                                    y: item.avg,
                                  };
                                })
                              : []
                          }
                          seriesName={['Work Environment', 'Previous Work Environment']}
                          color={colors.orangeBrand}
                          isNumber={true}
                          height={150}
                          width={'100%'}
                          strokeWidth={1}
                          showLabelOff={true}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} spacing={2}>
                  <Paper className={classes.papersColumn}>
                    <Grid
                      style={{
                        borderBottom: `2px solid ${colors.greyBg}`,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        padding: '5px 0',
                      }}
                    >
                      <Typography component="h2" className={classes.subtitle}>
                        {`EFFICACY: ${
                          activityReport.pillars.length
                            ? activityReport.pillars[activityReport.pillars.length - 1].efficacy >
                              0.8
                              ? 'Excellent Job'
                              : activityReport.pillars[activityReport.pillars.length - 1].efficacy >
                                0.6
                              ? 'Good Job'
                              : activityReport.pillars[activityReport.pillars.length - 1].efficacy >
                                0.4
                              ? 'It Should Be Better'
                              : 'We Must Do Better'
                            : ''
                        }`}
                      </Typography>
                      <Typography
                        onClick={() => history.push(`/dashboard/reports${selectedQuery}`)}
                        component="h2"
                        className={classes.ctaText}
                      >
                        Advanced Channel View <ArrowRightIcon fontSize="large" />
                      </Typography>
                    </Grid>
                    <Grid className={classes.gridRow}>
                      <Grid xs={4} className={classes.titleGrid}>
                        <Grid className={classes.gridRow}>
                          <PillarsAnimations speed={0.5} type="efficacy" containerWidth="45%" />
                          <SenslaChart
                            data={
                              activityReport
                                ? [
                                    activityReport.pillars[activityReport.pillars.length - 1]
                                      .efficacy,
                                  ]
                                : []
                            }
                            name="Efficacy"
                            color={colors.redBrand}
                          />
                        </Grid>
                        <Alert severity={'error'} icon={false}>
                          Efficacy is a metric that could be associated with teamwork, empathy, and
                          collaboration. This pillar is based on the team’s capacity to reply to
                          mentions and the average response time of the involved members. Remote
                          workers need answers to advance in some specific task or clarify doubts.
                        </Alert>
                      </Grid>
                      <Grid xs={2}>
                        <MentionsCounter
                          activityReport={activityReport}
                          withoutPaper={true}
                          withoutIcon={true}
                          withoutTime={true}
                        />
                        <div
                          style={{
                            border: `1px solid ${colors.textGrey3e}`,
                            opacity: 0.7,
                            width: '100%',
                          }}
                        />
                        <TimeCounter
                          activityReport={activityReport}
                          withoutPaper={true}
                          withoutIcon={true}
                          withoutTime={true}
                          withoutChip={true}
                        />
                      </Grid>
                      <Grid xs={6}>
                        <ColumnMiniChart
                          data={
                            activityReport
                              ? dataByDates(activityReport.mentionsDatesActivity, 'mentions').map(
                                  (item: any) => {
                                    return {
                                      x: moment(`${item.month}/${item.dayOfMonth}`),
                                      y: item.count,
                                    };
                                  }
                                )
                              : []
                          }
                          compareData={
                            activityReport
                              ? dataByDates(activityReport.mentionsDatesActivity, 'mentions')
                                  .map((i: any) => ({
                                    ...i,
                                    date: moment(`${i.month}/${i.dayOfMonth}`).toDate(),
                                  }))
                                  .sort((m1: any, m2: any) => m1.date - m2.date)
                                  .map((item: any) => {
                                    return {
                                      x: moment(`${item.month}/${item.dayOfMonth}`),
                                      y: item.respondedCount,
                                    };
                                  })
                              : []
                          }
                          seriesName={['Mentions', 'Responded Mentions']}
                          color={colors.redBrand}
                          isNumber={true}
                          height={150}
                          width={'100%'}
                          strokeWidth={1}
                          showLabelOff={true}
                          legendPosition="top"
                        />
                        <div
                          style={{
                            border: `1px solid ${colors.textGrey3e}`,
                            opacity: 0.7,
                            width: '100%',
                          }}
                        />
                        <LineMiniChart
                          data={
                            activityReport
                              ? dataByDates(activityReport.mentionsDatesActivity, 'mentions').map(
                                  (item: any) => {
                                    return {
                                      x: moment(`${item.month}/${item.dayOfMonth}`),
                                      y: item.avgTime / 60000,
                                    };
                                  }
                                )
                              : []
                          }
                          compareData={
                            activityReport
                              ? dataByDates(
                                  activityReport.previousMentionsDatesActivity,
                                  'mentions',
                                  true
                                ).map((item: any) => {
                                  return {
                                    x: moment(`${item.month}/${item.dayOfMonth}`),
                                    y: item.avgTime / 60000,
                                  };
                                })
                              : []
                          }
                          seriesName={['Avg Response Time', 'Previous Avg Response Time']}
                          color={colors.redBrand}
                          isNumber={true}
                          height={150}
                          width={'100%'}
                          strokeWidth={3}
                          showLabelOff={true}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} spacing={2}>
                  <Paper className={classes.papersColumn}>
                    <Grid
                      style={{
                        borderBottom: `2px solid ${colors.greyBg}`,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        padding: '5px 0',
                      }}
                    >
                      <Typography component="h2" className={classes.subtitle}>
                        {`CONSTANCY: ${
                          activityReport.pillars.length
                            ? activityReport.pillars[activityReport.pillars.length - 1].constancy >
                              0.8
                              ? 'Excellent Job'
                              : activityReport.pillars[activityReport.pillars.length - 1]
                                  .constancy > 0.6
                              ? 'Good Job'
                              : activityReport.pillars[activityReport.pillars.length - 1]
                                  .constancy > 0.4
                              ? 'It Should Be Better'
                              : 'We Must Do Better'
                            : ''
                        }`}
                      </Typography>
                      <Typography
                        onClick={() => history.push(`/dashboard/reports${selectedQuery}`)}
                        component="h2"
                        className={classes.ctaText}
                      >
                        Advanced Channel View <ArrowRightIcon fontSize="large" />
                      </Typography>
                    </Grid>
                    <Grid className={classes.gridRow}>
                      <Grid xs={4} className={classes.titleGrid}>
                        <Grid className={classes.gridRow}>
                          <PillarsAnimations speed={0.3} type="constancy" containerWidth="45%" />
                          <SenslaChart
                            data={
                              activityReport
                                ? [
                                    activityReport.pillars[activityReport.pillars.length - 1]
                                      .constancy,
                                  ]
                                : []
                            }
                            name="Constancy"
                            color={colors.greenBrand}
                          />
                        </Grid>
                        <Alert severity={'success'} icon={false}>
                          Constancy in remote workers means the capacity to be in place, interacting
                          with others day by day, maintaining the daily work in order. Constancy
                          does not mean extra work with extra time. Lower Constancy can determine
                          the absence of the team or some members of it. We define it with the
                          number of employees that are interacting within the group distributed by
                          weekdays.
                        </Alert>
                      </Grid>
                      <Grid xs={2}>
                        <ActivityCounter
                          activityReport={activityReport}
                          withoutPaper={true}
                          withoutIcon={true}
                        />
                        <div
                          style={{
                            border: `1px solid ${colors.textGrey3e}`,
                            opacity: 0.7,
                            width: '100%',
                          }}
                        />
                        <ActiveMembersCounter
                          activityReport={activityReport}
                          currentTeam={currentTeam}
                          channel={channel}
                          withoutPaper={true}
                          withoutIcon={true}
                        />
                      </Grid>
                      <Grid xs={6}>
                        <ColumnMiniChart
                          data={
                            activityReport
                              ? activityByWeek(activityReport.daysActivity).map((item: any) => {
                                  return { x: item.dateOfWeek, y: item.count };
                                })
                              : []
                          }
                          compareData={
                            activityReport
                              ? activityByWeek(activityReport.previousDaysActivity).map(
                                  (item: any) => {
                                    return { x: item.dateOfWeek, y: item.count };
                                  }
                                )
                              : []
                          }
                          seriesName={['Activity', 'Previous Activity']}
                          color={colors.greenBrand}
                          isNumber={true}
                          height={150}
                          width={'100%'}
                          strokeWidth={1}
                          showLabelOff={true}
                          isDay={true}
                          legendPosition="top"
                        />
                        <div
                          style={{
                            border: `1px solid ${colors.textGrey3e}`,
                            opacity: 0.7,
                            width: '100%',
                          }}
                        />
                        <ColumnMiniChart
                          data={
                            activityReport
                              ? activityByWeek(activityReport.daysActivity).map((item: any) => {
                                  return {
                                    x: item.dateOfWeek,
                                    y: item.memberCount ? item.memberCount.length : 0,
                                  };
                                })
                              : []
                          }
                          compareData={
                            activityReport
                              ? activityByWeek(activityReport.previousDaysActivity).map(
                                  (item: any) => {
                                    return {
                                      x: item.dateOfWeek,
                                      y: item.memberCount ? item.memberCount.length : 0,
                                    };
                                  }
                                )
                              : []
                          }
                          seriesName={['Active Members', 'Previous Active Members']}
                          color={colors.greenBrand}
                          isNumber={true}
                          height={150}
                          width={'100%'}
                          strokeWidth={1}
                          showLabelOff={true}
                          isDay={true}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </>
      ) : (
        <div className={classes.loading}>
          <Loading containerHeight="70vh" />
        </div>
      )}
    </div>
  );
};

export { PillarsReport };
